/* eslint-disable max-lines */
import { useModifyAlarmGroupUpdates } from '@hakimo-ui/hakimo/data-access';
import {
  LocationAlarmUpdate,
  ModifyAlarmGroupUpdatePayload,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { Alert, Button, InputField } from '@hakimo-ui/shared/ui-base';
import { CheckIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

interface SituationEditModalProps {
  update: LocationAlarmUpdate;
  alarmGroupId: string;
}

const getFormattedDate = (date?: string) => {
  if (!date) {
    return '-';
  }
  const dateObj = new Date(date);
  return dayjs(dateObj).format('DD/MM/YYYY, HH:mm:ss');
};

export function SituationEditModal(props: SituationEditModalProps) {
  const { update, alarmGroupId } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [situation, setSituation] = useState(update.situation || '');
  const [situationDescription, setSituationDescription] = useState(
    update.situation_description || ''
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const payload: ModifyAlarmGroupUpdatePayload = {
    situation: situation.trim(),
    situation_description: situationDescription.trim(),
  };

  const mutation = useModifyAlarmGroupUpdates(
    alarmGroupId,
    String(update.id),
    UnifiedMonitoringProduct.SCAN,
    payload,
    () => {
      // On success callback
      setShowSuccess(true);
      setIsEditing(false);
      // Update the local state to reflect the saved values
      update.situation = situation.trim();
      update.situation_description = situationDescription.trim();
      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
    },
    () => {
      // On error callback
      setShowError(true);
      // Auto-hide error message after 5 seconds
      setTimeout(() => setShowError(false), 5000);
    }
  );

  const handleSave = () => {
    setShowSuccess(false);
    setShowError(false);
    const payloadToSend: ModifyAlarmGroupUpdatePayload = {
      situation: situation.trim(),
      situation_description: situationDescription.trim(),
    };
    mutation.mutate(payloadToSend);
  };

  const handleCancel = () => {
    setSituation(update.situation || '');
    setSituationDescription(update.situation_description || '');
    setIsEditing(false);
    setShowSuccess(false);
    setShowError(false);
  };

  // Reset form when update changes
  useEffect(() => {
    setSituation(update.situation || '');
    setSituationDescription(update.situation_description || '');
  }, [update.situation, update.situation_description]);

  return (
    <div className="flex w-[40rem] flex-col gap-3 text-sm">
      {/* Header Section with Static Info */}
      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-4">
        <div className="grid gap-3">
          <div className="flex items-center justify-between">
            <span className="text-onlight-text-3 dark:text-ondark-text-2 text-xs font-medium">
              Update Type
            </span>
            <span className="text-onlight-text-2 dark:text-ondark-text-1 text-sm font-medium">
              {update.update_type}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-onlight-text-3 dark:text-ondark-text-2 text-xs font-medium">
              Update Time
            </span>
            <span className="text-onlight-text-2 dark:text-ondark-text-1 text-sm font-medium">
              {getFormattedDate(update.update_time)}
            </span>
          </div>

          {update.user && (
            <div className="flex items-center justify-between">
              <span className="text-onlight-text-3 dark:text-ondark-text-2 text-xs font-medium">
                User
              </span>
              <span className="text-onlight-text-2 dark:text-ondark-text-1 text-sm font-medium">
                {update.user.name}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Situation Fields Section */}
      <div className="border-onlight-line-2 dark:border-ondark-line-2 rounded-lg border">
        {/* Header with Edit Button */}
        <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 border-onlight-line-2 dark:border-ondark-line-2 flex items-center justify-between border-b px-4 py-2">
          <h3 className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold">
            Situation Details
          </h3>
          {!isEditing && (
            <Button variant="icon" onClick={() => setIsEditing(true)}>
              <PencilIcon className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        <div className="space-y-4 p-4">
          <div>
            <label className="text-onlight-text-3 dark:text-ondark-text-2 mb-1.5 block text-xs font-medium">
              Situation
            </label>
            {isEditing ? (
              <InputField
                type="text"
                value={situation}
                onChange={(e) => setSituation(e.target.value)}
                placeholder="Enter situation"
                autoFocus
              />
            ) : (
              <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 text-onlight-text-1 dark:text-ondark-text-1 min-h-[2.5rem] rounded-md px-3 py-2 text-sm">
                {situation || (
                  <span className="text-onlight-text-3 dark:text-ondark-text-3">
                    No situation specified
                  </span>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-onlight-text-3 dark:text-ondark-text-2 mb-1.5 block text-xs font-medium">
              Situation Description
            </label>
            {isEditing ? (
              <InputField
                type="text"
                value={situationDescription}
                onChange={(e) => setSituationDescription(e.target.value)}
                placeholder="Enter situation description"
              />
            ) : (
              <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 text-onlight-text-1 dark:text-ondark-text-1 min-h-[5rem] rounded-md px-3 py-2 text-sm">
                {situationDescription ? (
                  <span className="whitespace-pre-wrap">
                    {situationDescription}
                  </span>
                ) : (
                  <span className="text-onlight-text-3 dark:text-ondark-text-3">
                    No description provided
                  </span>
                )}
              </div>
            )}
          </div>

          {isEditing && (
            <div className="border-onlight-line-1 dark:border-ondark-line-1 flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={mutation.isLoading}
                loading={mutation.isLoading}
                className="flex gap-2"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={mutation.isLoading}
                loading={mutation.isLoading}
                className="flex gap-2"
              >
                <CheckIcon className="h-3.5 w-3.5" />
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      {showSuccess && (
        <Alert type="success" closeable onClose={() => setShowSuccess(false)}>
          Situation updated successfully
        </Alert>
      )}
      {showError && (
        <Alert type="error" closeable onClose={() => setShowError(false)}>
          {mutation.error?.message || 'Failed to update situation'}
        </Alert>
      )}
    </div>
  );
}

export default SituationEditModal;
